import { MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useCallback, useContext, useEffect, useMemo } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { toast } from "react-toastify"
import { z } from "zod"
import { digitalManagerApi } from "../../../../../services/api"
import {
  DiMaDetailsContent,
  DiMaDetailsContentProps,
} from "../../../../components/DiMaDetailsContent"
import { DiMaProgress } from "../../../../components/DiMaProgress"
import { UserContext } from "../../../../context/UserContext"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToFeature,
} from "../../../../models/Authorization"
import {
  NotificationTopicGet,
  NotificationTopicPut,
} from "../../../../models/NotificationTopic"

const validationSchema = z.object({
  name: z.string().min(1, "Name is required"),
})

const returnURL = `/notifications#topics`

export function TopicDetails(props: {
  readonly notificationTopic: NotificationTopicGet
  readonly setNotificationTopic: (updatedTopic: NotificationTopicGet) => void
}) {
  const { user, isLoadingUser } = useContext(UserContext)

  const { notificationTopic, setNotificationTopic } = props

  const formMethods = useForm<NotificationTopicPut>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
  })

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = formMethods

  useEffect(() => {
    reset({ name: notificationTopic.name })
  }, [notificationTopic, reset])

  const onSubmit = useCallback(
    async (values: NotificationTopicPut) => {
      try {
        const res = await digitalManagerApi.put<NotificationTopicGet>(
          `/notificationTopics/${notificationTopic.id}`,
          values
        )
        toast.success("Notification topic updated")
        setNotificationTopic(res.data)
      } catch {
        toast.error("Cannot update notification topic")
        throw new Error("Cannot update notification topic")
      }
    },
    [notificationTopic.id, setNotificationTopic]
  )

  const canWrite = useMemo(
    () =>
      user
        ? hasUserAccessToFeature(
            user.authorizations,
            DimaFeatureShortNameEnum.NM,
            AccessLevel.Write
          )
        : false,
    [user]
  )

  const handleTextFieldChange = useCallback(
    (field: ControllerRenderProps<NotificationTopicPut, "name">) => (value: string) =>
      field.onChange(value),
    []
  )

  const renderTextFieldName = useCallback(
    ({ field }: { field: ControllerRenderProps<NotificationTopicPut, "name"> }) => (
      <MUITextField
        id="name"
        type="text"
        size={32}
        name={field.name}
        value={field.value ?? ""}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={!canWrite}
        errors={
          typeof errors.name?.message === "string"
            ? { name: { message: errors.name.message } }
            : undefined
        }
      />
    ),
    [canWrite, errors.name, handleTextFieldChange]
  )

  if (isLoadingUser || !user) {
    return <DiMaProgress />
  }

  const detailsContentProps: DiMaDetailsContentProps<NotificationTopicPut> = {
    formMethods,
    canWrite,
    inlineEdit: true,
    listItems: [
      {
        id: 0,
        label: "Name",
        displayItem: <div style={{ width: "100%" }}>{notificationTopic.name}</div>,
        editItem: (
          <Controller name="name" control={control} render={renderTextFieldName} />
        ),
      },
    ],
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiMaDetailsContent {...detailsContentProps} onSubmit={onSubmit} />
    </form>
  )
}
